import type { SupabaseClient } from '@supabase/supabase-js';
import type { PulseCheckin, PulseInsights, StoredInsight } from '@/types/types';
import { getLocalDateString } from './shared';

export async function getTodayPulse(client: SupabaseClient, userId: string): Promise<PulseCheckin | null> {
    const today = getLocalDateString();
    const { data, error } = await client
        .from('pulse_checkins')
        .select('*')
        .eq('user_id', userId)
        .eq('date', today)
        .maybeSingle();
    if (error) throw error;
    return (data as PulseCheckin | null) ?? null;
}

export async function saveMorningCheckin(
    client: SupabaseClient,
    userId: string,
    data: {
        sleep_bed_time?: string | null;
        sleep_wake_time?: string | null;
        sleep_quality: number | null;
        morning_mood: number | null;
        morning_energy?: number | null;
        morning_feelings: string[];
        one_thing?: string | null;
        focus_tags?: string[];
    },
): Promise<PulseCheckin> {
    const today = getLocalDateString();
    const { data: result, error } = await client
        .from('pulse_checkins')
        .upsert({
            user_id: userId,
            date: today,
            ...data,
            morning_completed_at: new Date().toISOString(),
        }, { onConflict: 'user_id,date' })
        .select()
        .single();
    if (error) throw error;
    return result as PulseCheckin;
}

export async function saveEveningCheckin(
    client: SupabaseClient,
    userId: string,
    data: {
        evening_mood?: number | null;
        evening_performance: number | null;
        evening_stress: number | null;
        evening_motivation: number | null;
        evening_energy: number | null;
        evening_connectedness: number | null;
        evening_feelings: string[];
        one_thing_completed?: boolean | null;
        activity_tags: string[];
        reflection?: string | null;
    },
): Promise<PulseCheckin> {
    const today = getLocalDateString();
    const { data: result, error } = await client
        .from('pulse_checkins')
        .upsert({
            user_id: userId,
            date: today,
            ...data,
            evening_completed_at: new Date().toISOString(),
        }, { onConflict: 'user_id,date' })
        .select()
        .single();
    if (error) throw error;
    return result as PulseCheckin;
}

export async function resetPulseCheckin(client: SupabaseClient, userId: string, mode: 'morning' | 'evening'): Promise<void> {
    const today = getLocalDateString();

    const nullFields = mode === 'morning'
        ? {
            sleep_bed_time: null, sleep_wake_time: null, sleep_quality: null,
            morning_mood: null, morning_energy: null, morning_feelings: null,
            one_thing: null, morning_completed_at: null, focus_tags: null,
          }
        : {
            evening_mood: null, evening_performance: null, evening_stress: null,
            evening_motivation: null, evening_energy: null, evening_connectedness: null,
            evening_feelings: null, one_thing_completed: null, activity_tags: null,
            reflection: null, evening_completed_at: null,
          };

    const { error } = await client
        .from('pulse_checkins')
        .update(nullFields)
        .eq('user_id', userId)
        .eq('date', today);
    if (error) throw error;
}

export async function getPulseHistory(client: SupabaseClient, userId: string, days: number = 30): Promise<PulseCheckin[]> {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    const { data, error } = await client
        .from('pulse_checkins')
        .select('*')
        .eq('user_id', userId)
        .gte('date', getLocalDateString(startDate))
        .order('date', { ascending: false });
    if (error) {
        if (error.code === 'PGRST116') return [];
        throw error;
    }
    return (data || []) as PulseCheckin[];
}

export async function getPulseInsights(client: SupabaseClient, userId: string): Promise<PulseInsights | null> {
    const { data, error } = await client
        .from('pulse_insights')
        .select('*')
        .eq('user_id', userId)
        .order('computed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
    if (error) throw error;
    return (data as PulseInsights | null) ?? null;
}

export async function getPulseInsightHistory(client: SupabaseClient, userId: string, limit = 5): Promise<PulseInsights[]> {
    const { data, error } = await client
        .from('pulse_insights')
        .select('*')
        .eq('user_id', userId)
        .order('computed_at', { ascending: false })
        .limit(limit);
    if (error) {
        console.error('Error fetching insight history:', error.message);
        return [];
    }
    return (data || []) as PulseInsights[];
}

export async function upsertPulseInsights(
    client: SupabaseClient,
    userId: string,
    insights: StoredInsight[],
    dataDays: number,
    periodStart: string,
    periodEnd: string,
    moodLabel: string | null,
    summary?: string | null,
): Promise<void> {
    const { error } = await client
        .from('pulse_insights')
        .upsert({
            user_id: userId,
            computed_at: new Date().toISOString(),
            data_days: dataDays,
            period_start: periodStart,
            period_end: periodEnd,
            mood_label: moodLabel,
            insights,
            summary: summary ?? null,
        }, { onConflict: 'user_id,period_start' });
    if (error) throw error;
}

export async function deletePulseInsights(client: SupabaseClient, userId: string): Promise<void> {
    const { error } = await client
        .from('pulse_insights')
        .delete()
        .eq('user_id', userId);
    if (error) throw error;
}

export async function getUserActivityTags(client: SupabaseClient, userId: string): Promise<string[]> {
    const { data, error } = await client
        .from('pulse_checkins')
        .select('activity_tags')
        .eq('user_id', userId)
        .not('activity_tags', 'is', null);
    if (error) {
        console.error('getUserActivityTags error:', error.message);
        return [];
    }
    if (!data) return [];
    const tags = new Set<string>();
    for (const row of data) {
        if (Array.isArray(row.activity_tags)) {
            for (const tag of row.activity_tags) tags.add(tag);
        }
    }
    return Array.from(tags).sort();
}

export async function getPulseByDateRange(client: SupabaseClient, userId: string, startDate: string, endDate: string): Promise<PulseCheckin[]> {
    const { data, error } = await client
        .from('pulse_checkins')
        .select('id,date,sleep_bed_time,sleep_wake_time,sleep_quality,morning_mood,morning_energy,morning_feelings,morning_completed_at,focus_tags,evening_mood,evening_performance,evening_stress,evening_motivation,evening_energy,evening_connectedness,evening_feelings,evening_completed_at,activity_tags')
        .eq('user_id', userId)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });
    if (error) {
        console.error('Error fetching pulse by date range:', error.message);
        return [];
    }
    return (data || []) as PulseCheckin[];
}
